import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useOutsourceCategoriesQuery } from "../api/queries";

type OutsourcesSearch = {
  offset?: number;
  limit?: number;
  q?: string;
  categoryId?: string;
  payeeType?: "individual" | "team";
  status?: "active" | "inactive";
};

export function OutsourceFilter() {
  const nav = useNavigate({ from: "/app/performance/outsources" });
  const search = useSearch({
    from: "/app/performance/outsources",
  }) as OutsourcesSearch;

  const categoriesQuery = useOutsourceCategoriesQuery();
  const categories = categoriesQuery.data?.data ?? [];

  const [searchValue, setSearchValue] = useState(search.q ?? "");

  useEffect(() => {
    setSearchValue(search.q ?? "");
  }, [search.q]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    nav({
      search: {
        ...search,
        q: val.trim() || undefined,
        offset: 0,
      },
    });
  }, 400);

  const hasFilter =
    !!search.q?.trim() ||
    !!search.categoryId ||
    !!search.payeeType ||
    !!search.status;

  const categoryOptions = [
    { value: "all", label: "ທຸກຫມວດ" },
    ...categories
      .filter((c) => c.isActive)
      .map((c) => ({ value: c.id, label: `${c.nameLo} (${c.slug})` })),
  ];

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາຊື່ / ເບີ / ບັນຊີ / ຫມວດ..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-xs"
          />
          <SimpleSelect
            value={search.categoryId ?? "all"}
            onValueChange={(val) =>
              nav({
                search: {
                  ...search,
                  categoryId: val === "all" ? undefined : val,
                  offset: 0,
                },
              })
            }
            placeholder="ຫມວດ"
            className="h-8 w-full sm:w-44"
            options={categoryOptions}
          />
          <SimpleSelect
            value={search.payeeType ?? "all"}
            onValueChange={(val) =>
              nav({
                search: {
                  ...search,
                  payeeType:
                    val === "all" ? undefined : (val as "individual" | "team"),
                  offset: 0,
                },
              })
            }
            placeholder="ປະເພດ"
            className="h-8 w-full sm:w-36"
            options={[
              { value: "all", label: "ທຸກປະເພດ" },
              { value: "individual", label: "ບຸກຄົນ" },
              { value: "team", label: "ທີມ / ບໍລິສັດ" },
            ]}
          />
          <SimpleSelect
            value={search.status ?? "all"}
            onValueChange={(val) =>
              nav({
                search: {
                  ...search,
                  status:
                    val === "all"
                      ? undefined
                      : (val as "active" | "inactive"),
                  offset: 0,
                },
              })
            }
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "active", label: "ໃຊ້ງານ" },
              { value: "inactive", label: "ປິດໃຊ້" },
            ]}
          />
        </div>
        {hasFilter ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({
                search: {
                  offset: 0,
                  limit: search.limit,
                },
              })
            }
          >
            <XIcon className="size-4" /> ລ້າງ
          </Button>
        ) : null}
      </div>
    </div>
  );
}
